import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import type { HotelAmenityData } from '@apps/main/new-desktop/core/models';
import { getHotelAmenitiesConfig } from '@apps/main/new-desktop/core/models';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import { getGuestRating } from '@apps/main/new-desktop/modules/customersRating';

import type {
  DestinationProduct,
  HotelIngredientResponse,
  ContentApiImage,
  IngredientType,
  HotelAmenities,
  HotelAmenityType,
} from '@libs/trip-planner-interfaces';
import { AvailabilityOrder } from '@libs/trip-planner-interfaces';

import { HotelSelectionCardState } from '../../models';
import type {
  HotelSelectionCardData,
  HotelSelectionDestinationData,
} from '../../models';

export function getHotelSelectionHeaderData(
  stayIngredient: HotelIngredient | OwnStayIngredient | null,
  hotelIngredientResponse: HotelIngredientResponse | undefined,
  activeFiltersAmount: number
): HotelSelectionDestinationData | undefined {
  if (!stayIngredient) {
    return undefined;
  }

  return {
    destinationText: stayIngredient.displayText,
    numberOfNights:
      stayIngredient.numberOfNights ?? hotelIngredientResponse?.numberOfNights,
    activeFiltersAmount,
  };
}

export function filterHotels(
  hotelList: DestinationProduct[]
): DestinationProduct[] {
  const hasNoImage = (product: DestinationProduct): boolean =>
    product.images.length !== 0;
  const hasDescription = (product: DestinationProduct): boolean =>
    product.openingParagraph?.value !== undefined;

  return hotelList.filter(
    (product) =>
      product.totalPrice && hasNoImage(product) && hasDescription(product)
  );
}

export function sortHotels(
  hotelList: DestinationProduct[],
  sortBy: AvailabilityOrder
): DestinationProduct[] {
  if (sortBy === AvailabilityOrder.Cheapest) {
    return hotelList.sort((hotelA, hotelB) => {
      if (hotelA.totalPrice && hotelB.totalPrice) {
        return hotelA.totalPrice.amount - hotelB.totalPrice.amount;
      }
      return 0;
    });
  } else {
    return hotelList.sort(
      (hotelA, hotelB) => hotelB.popularityScore - hotelA.popularityScore
    );
  }
}

export function moveSelectedHotelToFirstPostion(
  initialHotelList: DestinationProduct[],
  hotelCode: string | undefined
): DestinationProduct[] {
  const hotelList = [...initialHotelList];

  if (hotelCode) {
    const hotelIndex = hotelList.findIndex(
      (product) => product.hotelCode === hotelCode
    );
    if (hotelIndex > 0) {
      const selectedHotel = hotelList.splice(hotelIndex, 1);
      hotelList.unshift(...selectedHotel);
    }
  }
  return hotelList;
}

export function mapAvailabilityOrderToSuggestionType(
  availabilityOrder: AvailabilityOrder
): SuggestionType {
  switch (availabilityOrder) {
    case AvailabilityOrder.Cheapest:
      return SuggestionType.Cheapest;

    case AvailabilityOrder.Popular:
      return SuggestionType.MostPopular;

    default:
      return SuggestionType.MostPopular;
  }
}

export function sortDestinationProducts(
  destinationProducts: DestinationProduct[],
  suggestionType: SuggestionType,
  selectedHotelCode?: string
): DestinationProduct[] {
  let availableProducts = filterHotels(destinationProducts);

  const availabilityOrder =
    suggestionType === SuggestionType.MostPopular
      ? AvailabilityOrder.Popular
      : AvailabilityOrder.Cheapest;

  availableProducts = sortHotels(availableProducts, availabilityOrder);

  if (availableProducts.length > 0) {
    if (selectedHotelCode) {
      availableProducts = moveSelectedHotelToFirstPostion(
        availableProducts,
        selectedHotelCode
      );
    }
  }

  return availableProducts;
}

interface MapDestinationProductToHotelSelectionCardParams {
  product: DestinationProduct;
  selectedHotelCode: string | undefined;
  ingredientIndex: number;
  previouslySeenHotelCodes: string[];
  isSuggestedHotel: boolean;
  recipeIngredientTypes: IngredientType[];
  recipeWithoutOwnTransportIngredientIndex: number;
  pax: string;
  formatMoney: (amount: number, currency?: string) => string;
  getImageUrl: (
    image?: ContentApiImage,
    hotelCode?: string,
    width?: number,
    height?: number
  ) => string;
}

function getTopHotelAmenities(
  amenities: HotelAmenities | undefined,
  amount: number
): HotelAmenityData[][] {
  if (!amenities || Object.keys(amenities).length === 0) {
    return [];
  }

  const config = getHotelAmenitiesConfig();
  const amenityTypes = Object.keys(amenities) as HotelAmenityType[];
  const topAmenities = amenityTypes
    .filter((key) => Boolean(amenities[key]))
    .slice(0, amount)
    .map((key) => config[key])
    .sort((a, b) => b.value.length - a.value.length);

  return [
    topAmenities.slice(topAmenities.length / 2),
    topAmenities.slice(0, topAmenities.length / 2),
  ];
}

export function mapDestinationProductToHotelSelectionCard(
  params: MapDestinationProductToHotelSelectionCardParams
): HotelSelectionCardData {
  const subtitle = getHotelSelectionCardSubtitle(params.product);

  const MAX_AMENITIES = 4;
  const amenities = params.product.amenities
    ? getTopHotelAmenities(params.product.amenities, MAX_AMENITIES)
    : [];

  const content = openingParagraphToBulletPoints(
    params.product.openingParagraph?.value || ''
  );

  const guestRating = getGuestRating(params.product.guestRatings);

  return {
    hotelSelectionCardContent: {
      title: params.product.name,
      subtitle,
      stars: params.product.semboSunRating,
      content,
      freeCancellation: false,
      amenities,
      guestRating,
      boardBasis: params.product.boardBasis,
    },
    hotelSelectionCardSummary: {
      totalPrice: params.formatMoney(
        params.product.totalPrice?.amount || 0,
        params.product.totalPrice?.currency
      ),
      grossPrice: params.product.grossPrice
        ? params.formatMoney(
            params.product.grossPrice.amount,
            params.product.grossPrice.currency
          )
        : '',
      pax: params.pax,
      recipeIngredientTypes: params.recipeIngredientTypes,
      recipeWithoutOwnTransportIngredientIndex:
        params.recipeWithoutOwnTransportIngredientIndex,
    },
    imageUrls: params.product.images.map((image) => params.getImageUrl(image)),
    context: {
      hotelCode: params.product.hotelCode,
      ingredientIndex: params.ingredientIndex,
      accommodationType: params.product.typeOfHotel,
      isPreviouslySeen: params.previouslySeenHotelCodes.includes(
        params.product.hotelCode
      ),
      selectionState: getSelectionState(
        params.isSuggestedHotel,
        params.product.hotelCode === params.selectedHotelCode
      ),
    },
  };
}

function getSelectionState(
  isSuggestedHotel: boolean,
  isSelected: boolean
): HotelSelectionCardState {
  if (isSuggestedHotel) {
    return HotelSelectionCardState.Suggested;
  } else if (isSelected) {
    return HotelSelectionCardState.Selected;
  } else {
    return HotelSelectionCardState.None;
  }
}

export function getHotelSelectionCardSubtitle(
  product: DestinationProduct
): string {
  if (product.mainPolygon) {
    return product.mainPolygon.fullName.toLocaleLowerCase();
  }

  if (product.address?.city) {
    return product.address.country
      ? `${product.address.city}, ${product.address.country}`.toLocaleLowerCase()
      : product.address.city.toLocaleLowerCase();
  }

  return '';
}

export function openingParagraphToBulletPoints(value: string): string[] {
  const bulletPointAmount = 3;

  return value
    .split('\n')
    .map((item: string) => item.replaceAll('**', ''))
    .map((item: string) => item.replaceAll('\r', ''))
    .map((item: string) => {
      const text = item.match(/[a-zA-Z].*/);
      return text ? text[0] : item;
    })
    .filter((item: string) => item !== '')
    .slice(0, bulletPointAmount);
}
